import { createResolverRegistry } from '../resolvers/registry/resolverRegistry';
import { ResolverState } from '../resolvers/definition/resolverState';
import { AnyResolver } from '../resolvers/definition/resolver';
import { createResourceLifecycleManager } from '../lifecycleManager/resourceLifecycleManager';
import { create } from 'event-emitter';
import {
  ResourceClient,
  OptionalGetPromiseArgs,
  OptionalInvalidateArgs,
  OptionalRefetchArgs,
  OptionalRetainArgs,
  OptionalSetStateArgs,
  WatchArgs,
  WatchCallback,
  WatchStatesCallback,
  GetStateArgs,
  GetStatesArgs,
  ResourceInstanceState,
} from './types';
import {
  ResourceRegistry,
  defaultResourceRegistry,
} from '../resources/resourceRegistry';
import { ChangeContext, createChangeManager } from './changeManager';
import { createPollManager } from '../polling/pollManager';
import { createUnwatchManager } from './unwatchManager';
import { createGroupManager } from '../groups/groupManager';
import { createEmissionBuffer } from './emissionBuffer';
import { AnyResourceDefinition } from '../resources/types';
import { indexedMap } from '../utils/indexedMap';
import { ResourcePromise, SuspendedState, PromiseMode } from './types';
import { withResolvers, withTrackedResolvers } from '../utils/withResolvers';
import { createLogger } from '../devtools/logger';
import { Metrics } from '../metrics/metrics';
import { maybeLoadDevtools } from '../devtools/maybeLoadDevtools';
import { processStack } from '../devtools/processStack';
import { type WatcherAttribution } from '../devtools/deferred/messages';
import {
  __mockInternal_isEnabled,
  __mockInternal_getMockResolver,
  __mockInternal_registerClient,
} from '../mockInternal';
import { createPersistenceManager } from '../persistence/persistenceManager';
import {
  peerSyncManager,
  PEER_SYNC_DEFAULT_MAX_AGE,
} from '../granite/peerSync';
import { toLoading, toSuccess } from '../resolvers/definition/resolverState';
import { parseTypeName } from '../resources/resource';
import { createVisibilityManager } from '../visibility/visibilityManager';

let __TEST_ONLY_DISABLE_DEFER_UNWATCH = false;

export const __testOnlyDisableDeferUnwatch = () => {
  __TEST_ONLY_DISABLE_DEFER_UNWATCH = true;
};

export const __testOnlyEnableDeferUnwatch = () => {
  __TEST_ONLY_DISABLE_DEFER_UNWATCH = false;
};

type StateEvents = {
  [key: string]: ResolverState<any, any>;
};

type CreateClientOptions = {
  resourceRegistry?: ResourceRegistry;
};

const constructClient = ({
  resourceRegistry = defaultResourceRegistry,
}: CreateClientOptions = {}): ResourceClient => {
  const promiseCache = indexedMap<ResourcePromise<any, any, PromiseMode>>();

  const logger = createLogger();

  const clientId = crypto.randomUUID();

  const devtools = maybeLoadDevtools({ clientId, logger });

  const changeManager = createChangeManager({
    devtools: devtools.callbacks,
  });

  logger?.debug('startup');

  const stateEmitter = create<StateEvents>({
    onEmitError: ({ error }) => {
      logger?.error('error emitting state', { error: error.message });

      return false;
    },
  });

  const statesEmitter = create<{ [typeName: string]: void }>({
    onEmitError: ({ error }) => {
      logger?.error('error emitting states change', { error: error.message });

      return false;
    },
  });

  const emissionBuffer = createEmissionBuffer({
    onFlush: (instanceKey, state) => {
      if (!state.loading || promiseCache.get(instanceKey, 'fresh')?._resolved) {
        promiseCache.delete(instanceKey, 'fresh');
      }

      if (!state.loading || promiseCache.get(instanceKey, 'stale')?._resolved) {
        promiseCache.delete(instanceKey, 'stale');
      }

      stateEmitter.emit(instanceKey, state);
    },
    onFlushTypes: (typeNames) => {
      for (const typeName of typeNames) {
        statesEmitter.emit(typeName);
      }
    },
  });

  const persistenceManager = createPersistenceManager();

  let getStoredAt = (__instanceKey: string): number | undefined => undefined;

  const resolverRegistry = createResolverRegistry({
    logger: logger?.withTags({ component: 'resolverRegistry' }),
    persistenceManager,
    getStoredAt: (instanceKey) => getStoredAt(instanceKey),
  });

  const groupManager = createGroupManager({
    logger: logger?.withTags({ component: 'groupManager' }),
  });

  let collectPeerSync = (
    __instanceKey: string,
    __data: unknown,
    __storedAt: number | undefined,
    __stale: boolean
  ) => {};

  let requestRefresh = (
    __instanceKey: string,
    __changeContext?: ChangeContext
  ) => {};

  const lifecycleManager = createResourceLifecycleManager({
    devtools: devtools.callbacks,
    logger: logger?.withTags({ component: 'lifecycleManager' }),
    resourceRegistry,
    groupManager,
    changeManager,
    persistenceManager,
    onNodeCollected: (instanceKey) => {
      promiseCache.delete(instanceKey, 'fresh');
      promiseCache.delete(instanceKey, 'stale');

      devtools.callbacks?.onNodeCollected(instanceKey);
      statesEmitter.emit(parseTypeName(instanceKey));
    },
    onStateChange: (instanceKey, state, changeContext, dataChanged) => {
      emissionBuffer.schedule(instanceKey, state);

      if (
        dataChanged &&
        !state.loading &&
        !state.error &&
        !state.stale &&
        state.data !== undefined &&
        !changeContext.optimistic &&
        changeContext.origin !== 'peer-sync'
      ) {
        collectPeerSync(
          instanceKey,
          state.data,
          lifecycleManager.getStoredAt(instanceKey),
          state.stale
        );
      }

      logger?.debug('received state change notification', {
        instanceKey,
        changeId: changeContext.changeId,
        originId: changeContext.originId,
        loading: state.loading,
        stale: state.stale,
        error: !!state.error,
      });
    },
    onInvalidate: ({ instanceKey, triggeringContext }) => {
      const nextContext = changeManager.cascade(triggeringContext);

      const invalidateLogger = logger?.withTags({
        instanceKey,
        changeId: nextContext?.changeId,
        originId: nextContext?.originId,
      });

      invalidateLogger?.info('received invalidation request event');

      if (resolverRegistry.isAttached(instanceKey)) {
        invalidateLogger?.info('requesting resolver refresh');

        requestRefresh(instanceKey, nextContext);
      } else {
        invalidateLogger?.debug(
          'requested invalidation of non-attached resource'
        );
      }
    },
  });

  getStoredAt = lifecycleManager.getStoredAt;

  const peerSync = peerSyncManager({
    clientId,
    getState: (instanceKey) => lifecycleManager.getState(instanceKey),
    getStoredAt: (instanceKey) => lifecycleManager.getStoredAt(instanceKey),
    applyBatch: (changes) => {
      const changeContext = changeManager.start('peer-sync');

      let applied = 0;
      for (const { instanceKey, data, storedAt } of changes) {
        const params = lifecycleManager.getParams(instanceKey);

        if (params !== undefined) {
          lifecycleManager.handleStateChange(
            instanceKey,
            params,
            changeContext,
            toSuccess(data)(),
            storedAt
          );
          applied += 1;
        }
      }

      logger?.info('applied peer-sync batch', {
        component: 'peerSync',
        received: changes.length,
        applied,
      });

      emissionBuffer.propagationFinished();
    },
  });

  collectPeerSync = peerSync.collect;

  const propagationFinished = () => {
    emissionBuffer.propagationFinished();
    peerSync.flush();
  };

  const unwatchManager = createUnwatchManager(lifecycleManager.detach);

  const isExpired = (instanceKey: string) => {
    const { staleTime } = resourceRegistry.getResource(instanceKey);

    return (
      staleTime !== undefined &&
      lifecycleManager.isExpired(instanceKey, staleTime)
    );
  };

  const visibilityManager = createVisibilityManager({
    logger: logger?.withTags({ component: 'visibilityManager' }),
    getAttachedKeys: () => resolverRegistry.getAttachedKeys(),
    isSettled: (instanceKey) => lifecycleManager.isSettled(instanceKey),
    hasData: (instanceKey) =>
      lifecycleManager.getState(instanceKey)?.data !== undefined,
    isExpired,
    isDefinitionKeptAlive: (instanceKey) =>
      !!resourceRegistry.getResource(instanceKey).keepAliveWhenHidden,
    refresh: (
      instanceKey,
      changeContext = changeManager.cascade(changeManager.start('visibility'))
    ) => {
      if (resolverRegistry.isAttached(instanceKey)) {
        resolverRegistry.refresh(instanceKey, changeContext);
      }
    },
    suspend: (instanceKey) => resolverRegistry.suspend(instanceKey),
    resume: (instanceKey) => resolverRegistry.resume(instanceKey),
  });

  requestRefresh = visibilityManager.requestRefresh;

  const refreshInstance = (instanceKey: string) => {
    if (resolverRegistry.isAttached(instanceKey)) {
      visibilityManager.requestRefresh(
        instanceKey,
        changeManager.start('poll')
      );
    }
  };

  const pollManager = createPollManager({
    logger: logger?.withTags({ component: 'pollManager' }),
    onPoll: (key) => {
      if (groupManager.has(key)) {
        for (const instanceKey of groupManager.get(key)!) {
          refreshInstance(instanceKey);
        }
      } else {
        refreshInstance(key);
      }
    },
  });

  let nextWatcherId = 0;

  const watch = <TParams, TData, TError extends Error = Error>(
    {
      resource,
      params = {} as TParams,
      instanceKey = resource.identifyInstance(params),
      pollInterval,
      httpClient,
      pollGroup,
      passive = false,
      keepAliveWhenHidden = false,
    }: WatchArgs<TParams, TData, TError>,
    callback?: WatchCallback<TData, TError>,
    __attribution?: WatcherAttribution
  ) => {
    const watchLogger = logger?.withTags({
      instanceKey,
    });

    watchLogger?.info('watching resource');

    const watcherId = nextWatcherId++;
    devtools.callbacks?.onWatcherAdded(
      watcherId,
      instanceKey,
      __attribution ?? {
        kind: 'external',
        stack: processStack(new Error()),
      }
    );

    unwatchManager.clear(instanceKey);

    // Mock interception (test-only, DCE'd in production)
    let __mockResolver: AnyResolver | null = null;
    if (process.env.NODE_ENV !== 'production') {
      if (__mockInternal_isEnabled()) {
        __mockResolver = __mockInternal_getMockResolver(resource, instanceKey);
      }
    }

    // Watch sources
    // Mock boundary: skip source watching when a mock resolver is active
    let sourceUnwatches: (() => void)[] | null = null;
    if (resource.sources && !__mockResolver) {
      for (const source of resource.sources) {
        (sourceUnwatches ??= []).push(
          watch(
            {
              resource: source.resource,
              params: source.mapParams(params),
              pollInterval,
              httpClient,
              pollGroup,
              passive,
              keepAliveWhenHidden,
            },
            undefined,
            { kind: 'derived', parentInstanceKey: instanceKey }
          )
        );
      }
    }

    // If needed, register the poll interval
    let unregisterPoll: (() => void) | undefined;
    if (pollInterval && pollInterval > 0) {
      if (pollGroup) {
        groupManager.add(pollGroup, instanceKey);

        const unregisterInterval = pollManager.register(
          pollGroup,
          pollInterval
        );

        unregisterPoll = () => {
          unregisterInterval();
          groupManager.remove(pollGroup, instanceKey);
        };
      } else {
        unregisterPoll = pollManager.register(instanceKey, pollInterval);
      }
    }

    const releaseKeepAlive = keepAliveWhenHidden
      ? visibilityManager.keepAlive(instanceKey)
      : undefined;

    // Subscribe to state changes
    // Note: The callback's fallback must be a new reference per-watch
    const off = stateEmitter.on(
      instanceKey,
      callback ? (state) => callback(state, unwatch) : () => {}
    );

    // Undo all the work we just did
    function unwatch() {
      watchLogger?.info('unwatching resource');

      devtools.callbacks?.onWatcherRemoved(watcherId);

      // Unwatch sources and unregister poll for this watcher unconditionally.
      // Each source has its own refcount, so this cascades correctly.
      // pollManager has its own internal refcounting per interval.
      if (sourceUnwatches) {
        for (const unwatchSource of sourceUnwatches) {
          unwatchSource();
        }
      }

      unregisterPoll?.();
      releaseKeepAlive?.();

      if (!passive) {
        resolverRegistry.detach(instanceKey);
      }

      if (off() === 0) {
        if (__TEST_ONLY_DISABLE_DEFER_UNWATCH) {
          lifecycleManager.detach(instanceKey);
        } else {
          unwatchManager.queue(instanceKey);
        }
      }
    }

    if (
      !passive &&
      !resource.sources &&
      lifecycleManager.getState<TData>(instanceKey)?.data === undefined
    ) {
      const peer = peerSync.pull(instanceKey);

      if (peer !== undefined) {
        // Within the freshness window we adopt the peer's value as-is and skip
        // the fetch. Past it (or if the peer itself flagged it stale) we adopt
        // on the stale side — data shows immediately while attach triggers a
        // refresh — so bounced values can't stay locked in indefinitely.
        const maxAge = resource.peerSyncMaxAge ?? PEER_SYNC_DEFAULT_MAX_AGE;
        const isStale =
          peer.stale || Date.now() - (peer.storedAt ?? 0) >= maxAge;

        const success = toSuccess(peer.data as TData)();

        lifecycleManager.handleStateChange(
          instanceKey,
          params,
          changeManager.start('peer-sync'),
          isStale ? toLoading()(success) : success,
          peer.storedAt
        );
        propagationFinished();

        if (isStale) {
          logger?.info('adopted stale peer value; triggering refresh', {
            component: 'peerSync',
            instanceKey,
            storedAt: peer.storedAt,
            age: Date.now() - (peer.storedAt ?? 0),
            peerFlaggedStale: peer.stale,
          });
        } else {
          logger?.info('initialized from peer', {
            component: 'peerSync',
            instanceKey,
          });
        }
      }
    }

    const resolverToAttach = __mockResolver ?? resource.resolver;

    const priorState = lifecycleManager.getState<TData>(instanceKey);
    const isFirstWatcher = () =>
      !!resolverToAttach &&
      !passive &&
      !resolverRegistry.isReferenced(instanceKey);
    const failedWithNoData = () =>
      !!priorState?.error && priorState.data === undefined;
    const retryFailedLoad =
      resource.retryOnMount === true && isFirstWatcher() && failedWithNoData();

    let didAttachResolver = false;
    if (resolverToAttach && !passive) {
      watchLogger?.info('attaching resolver');

      didAttachResolver = resolverRegistry.attach({
        resource,
        instanceKey,
        params,
        resolver: resolverToAttach,
        httpClient,
        getState: () => lifecycleManager.getState<TData>(instanceKey),
        onResolverUpdate: (resolverState, meta) => {
          watchLogger?.info('received state update from resolver', {
            changeId: meta?.changeContext?.changeId,
            originId: meta?.changeContext?.originId,
            loading: resolverState.loading,
            stale: resolverState.stale,
            error: !!resolverState.error,
          });

          lifecycleManager.handleStateChange(
            instanceKey,
            params,
            meta?.changeContext
              ? changeManager.next(meta.changeContext)
              : changeManager.start('resolver-update'),
            resolverState,
            meta?.storedAt,
            Date.now()
          );
          propagationFinished();
        },
      });
    }

    if (!lifecycleManager.isAttached(instanceKey)) {
      watchLogger?.info('attaching resource');

      lifecycleManager.attach({
        params,
        instanceKey,
        // Treat initializations as cascades so that we don't trigger
        // downstream invalidations. This is an (admittedly imperfect) method to
        // prevent refresh storms when lots of invalidation-linked nodes all init at once
        changeContext: changeManager.cascade(changeManager.start('init')),
      });
      propagationFinished();
    } else if (didAttachResolver) {
      const currentState = lifecycleManager.getState<TData>(instanceKey);

      if (!currentState || currentState.loading || currentState.stale) {
        visibilityManager.requestRefresh(
          instanceKey,
          changeManager.cascade(changeManager.start('init'))
        );
      }
    }

    if (retryFailedLoad) {
      watchLogger?.info('retrying failed first load');
      Metrics.counter('retry-on-mount').increment();

      visibilityManager.requestRefresh(
        instanceKey,
        changeManager.cascade(changeManager.start('init'))
      );
    } else if (resolverToAttach && !passive && isExpired(instanceKey)) {
      watchLogger?.info('refreshing expired resource');

      visibilityManager.requestRefresh(
        instanceKey,
        changeManager.cascade(changeManager.start('init'))
      );
    }

    return unwatch;
  };

  const getState = <TParams, TData>({
    resource,
    params = {} as TParams,
    instanceKey = resource.identifyInstance(params),
    materialize = true,
  }: GetStateArgs<TParams, TData>) => {
    const existing = lifecycleManager.getState<TData>(instanceKey);

    if (existing || !materialize) {
      return existing;
    }

    const state = lifecycleManager.materialize<TData>({
      instanceKey,
      params,
      changeContext: changeManager.start('materialize'),
    });
    propagationFinished();
    return state;
  };

  const invalidate = <TParams, TData>({
    resource,
    params = {} as TParams,
    instanceKey = resource.identifyInstance(params),
  }: OptionalInvalidateArgs<TParams, TData>) => {
    const changeContext = changeManager.start('invalidate');
    const invalidateLogger = logger?.withTags({
      instanceKey,
      changeId: changeContext.changeId,
      originId: changeContext.originId,
    });

    invalidateLogger?.info('invalidating resource');

    lifecycleManager.invalidate(instanceKey, changeContext);
    propagationFinished();
  };

  const invalidateAll = ({
    resource: { typeName },
  }: {
    resource: AnyResourceDefinition;
  }) => {
    const changeContext = changeManager.start('invalidate-all');
    const invalidateLogger = logger?.withTags({
      typeName,
      changeId: changeContext.changeId,
      originId: changeContext.originId,
    });

    invalidateLogger?.info('invalidating all resources of type');

    lifecycleManager.invalidateAll(typeName, changeContext);
    propagationFinished();
  };

  const retain = <TParams, TData>({
    resource,
    params = {} as TParams,
    instanceKey = resource.identifyInstance(params),
    duration,
  }: OptionalRetainArgs<TParams, TData>) => {
    logger?.info('retaining resource', { instanceKey, duration });

    lifecycleManager.retain({ instanceKey, params, period: duration });
    propagationFinished();

    if (resource.sources) {
      for (const source of resource.sources) {
        retain({
          resource: source.resource,
          params: source.mapParams(params),
          duration,
        });
      }
    }
  };

  const getStates = <TParams, TData, TError extends Error = Error>({
    resource,
  }: GetStatesArgs<TParams, TData, TError>): ResourceInstanceState<
    TParams,
    TData,
    TError
  >[] =>
    lifecycleManager.getInstancesForType<TParams, TData, TError>(
      resource.typeName
    );

  const watchStates = <TParams, TData, TError extends Error = Error>(
    { resource }: GetStatesArgs<TParams, TData, TError>,
    callback: WatchStatesCallback<TParams, TData, TError>
  ) =>
    statesEmitter.on(resource.typeName, (__data, off) =>
      callback(getStates({ resource }), off)
    );

  const setState = <TParams, TData>({
    resource,
    state,
    params = {} as TParams,
    instanceKey = resource.identifyInstance(params),
    optimistic = false,
  }: OptionalSetStateArgs<TParams, TData>) => {
    const changeContext = changeManager.start('set-state', { optimistic });
    const setStateLogger = logger?.withTags({
      instanceKey,
      changeId: changeContext.changeId,
      originId: changeContext.originId,
    });

    const nextState =
      typeof state === 'function'
        ? state(lifecycleManager.getState<TData>(instanceKey))
        : state;

    if (nextState) {
      setStateLogger?.info('setting state');

      let unwatch: (() => void) | undefined;

      // watch the full chain for derived resources to ensure all links are available
      if (resource.sources && !lifecycleManager.isAttached(instanceKey)) {
        unwatch = watch(
          {
            resource,
            params,
            instanceKey,
            passive: true,
          } as WatchArgs<TParams, TData>,
          undefined,
          { kind: 'internal', reason: 'set-state' }
        );
      }

      lifecycleManager.handleStateChange(
        instanceKey,
        params,
        changeContext,
        nextState
      );
      propagationFinished();

      // Unwatch after — not strictly necessary at runtime as detachment is deferred,
      // but tests disable the deferral
      unwatch?.();
    } else {
      setStateLogger?.debug('functional state update returned void');
    }
  };

  const getPromise = <TParams, TData>({
    resource,
    params = {} as TParams,
    instanceKey = resource.identifyInstance(params),
    resolveWithStale = false,
    httpClient,
  }: OptionalGetPromiseArgs<TParams, TData>) => {
    const promiseType = resolveWithStale ? 'stale' : 'fresh';

    const current = promiseCache.get(instanceKey, promiseType);

    if (current) {
      return current;
    }

    const { promise, resolve, reject } =
      withTrackedResolvers<SuspendedState<TData, Error, PromiseMode>>();

    const hasData = (
      s: ResolverState<TData>
    ): s is ResolverState<TData> & { data: TData } => s.data !== undefined;

    const handleStateChange = (state?: ResolverState<TData>) => {
      if (!state) {
        return false;
      }

      if (state.error && !(resolveWithStale && hasData(state))) {
        reject(state.error);
        return true;
      }

      // Fresh mode: only settled, non-stale data resolves. A stale instance
      // falls through so getPromise attaches and refetches.
      if (
        !resolveWithStale &&
        !state.loading &&
        !state.error &&
        !state.stale &&
        hasData(state)
      ) {
        resolve(state);
        return true;
      }

      // Stale mode: resolve as soon as any data exists, even while loading.
      if (resolveWithStale && hasData(state)) {
        resolve(state);
        return true;
      }

      return false;
    };

    const materialized = lifecycleManager.materialize<TData>({
      instanceKey,
      params,
      changeContext: changeManager.start('materialize'),
    });
    propagationFinished();

    if (handleStateChange(materialized)) {
      promiseCache.set(instanceKey, promiseType, promise);

      return promise;
    }

    watch(
      {
        resource,
        params,
        instanceKey,
        httpClient,
      } as WatchArgs<TParams, TData>,
      (state, unwatch) => {
        if (handleStateChange(state)) {
          unwatch();
        }
      },
      { kind: 'internal', reason: 'get-promise' }
    );

    promiseCache.set(instanceKey, promiseType, promise);

    return promise;
  };

  const refreshResolvers = (
    resource: AnyResourceDefinition,
    params: unknown,
    instanceKey: string,
    changeContext: ChangeContext
  ) => {
    if (resolverRegistry.isAttached(instanceKey)) {
      resolverRegistry.refresh(instanceKey, changeContext);
      return;
    }

    for (const source of resource.sources ?? []) {
      const sourceParams = source.mapParams(params);

      refreshResolvers(
        source.resource,
        sourceParams,
        source.resource.identifyInstance(sourceParams),
        changeContext
      );
    }
  };

  const refetch = <TParams, TData>({
    resource,
    params = {} as TParams,
    instanceKey = resource.identifyInstance(params),
    httpClient,
  }: OptionalRefetchArgs<TParams, TData>) => {
    const changeContext = changeManager.start('refetch');

    logger?.info('refetching resource', {
      instanceKey,
      changeId: changeContext.changeId,
      originId: changeContext.originId,
    });

    const { promise, resolve, reject } = withResolvers<SuspendedState<TData>>();

    const settle = (state?: ResolverState<TData>) => {
      if (state?.error) {
        reject(state.error);
        return true;
      }

      if (state && !state.loading && !state.stale && state.data !== undefined) {
        resolve(state as SuspendedState<TData>);
        return true;
      }

      return false;
    };

    const unwatch = watch(
      { resource, params, instanceKey, httpClient } as WatchArgs<
        TParams,
        TData
      >,
      (state, off) => {
        if (settle(state)) {
          off();
        }
      },
      { kind: 'internal', reason: 'refetch' }
    );

    refreshResolvers(resource, params, instanceKey, changeContext);

    if (settle(lifecycleManager.getState<TData>(instanceKey))) {
      unwatch();
    }

    return promise;
  };

  resourceRegistry.emitter.on('schemaChange', (event) => {
    lifecycleManager.resync({
      added: event.added,
      removed: event.removed,
      changeContext: changeManager.start('schema-change'),
    });
  });

  resourceRegistry.emitter.on('resourceRemoved', ({ typeName }) => {
    const changeContext = changeManager.start('resource-removed');

    const instances = groupManager.get(typeName);
    if (instances) {
      for (const instanceKey of [...instances]) {
        resolverRegistry.forceDetach(instanceKey);
      }
    }

    resolverRegistry.evictType(typeName);
    lifecycleManager.removeAllOfType(typeName, changeContext);
    propagationFinished();
  });

  devtools.registerHooks({
    resourceRegistry,
    resolverRegistry,
    lifecycleManager,
    pollManager,
    invalidate,
    setState,
    getPollGroupMembers: () => groupManager.getAllMembers(),
    persistenceManager,
  });

  if (process.env.NODE_ENV !== 'production') {
    __mockInternal_registerClient({
      getInstancesForType: (typeName: string) =>
        lifecycleManager.getInstancesForType(typeName),
    });
  }

  return {
    getState,
    getStates,
    watchStates,
    watch,
    getPromise,
    refetch,
    invalidate,
    invalidateAll,
    retain,
    setState,
    // Used as a feature flag — null means devtools are off, non-null enables
    // stack capture in hooks. Don't set unconditionally.
    devtoolsId: devtools.devtoolsId,
    teardown: () => {
      peerSync.destroy();
      visibilityManager.destroy();
    },
  };
};

let _singleton: ResourceClient | undefined;

/**
 * Returns the module-level Pulse client, constructing it on first call.
 * Subsequent calls return the same instance — Pulse is intentionally a
 * singleton so hooks, libraries, and app code all share one cache.
 */
export const createClient = (): ResourceClient => {
  if (!_singleton) {
    _singleton = constructClient();
  }
  return _singleton;
};

/**
 * Returns the Pulse client singleton. This is the recommended way to obtain
 * a reference to the client outside of React (inside React, use the hooks).
 */
export const getPulseClient = (): ResourceClient => createClient();

export const __testOnly_clearClient = () => {
  _singleton = undefined;
};

export const __testOnly_teardownClientIfExists = () => {
  _singleton?.teardown();
  _singleton = undefined;
};

export const __testOnly_constructClient = (opts?: {
  resourceRegistry?: ResourceRegistry;
}) => {
  const client = constructClient(opts);

  return client;
};
